import React, { useState } from 'react';
import { Button, FieldGroup } from '@ver-uds/react';
import { Alert } from '@ver-uds/uswds-react';
import { format } from 'date-fns';
import { Account, AccountType } from 'eve-account-client';
import { useAuthState } from '@vis-auth/react';
import FullWidthLayout from '../../layouts/FullWidthLayout';
import HelpTextConstants from '../../constants/HelpTextConstants';
import CancelTerminateAccountModal from '../CancelTerminateAccountModal';
import ErrorBar from '../ErrorBar';

export type FormValues = {
  dunsNumber?: string;
};

export type TerminationRequestSummaryProps = {
  accountData: Account;
  accountType: AccountType;
  title?: string;
  description?: string;
};

const TerminationRequestSummary = ({
  accountData,
  accountType,
  title,
  description,
}: TerminationRequestSummaryProps): JSX.Element => {
  const { user } = useAuthState();

  // eslint-disable-next-line camelcase
  const accountId = accountData.id || (user?.account_id as string);

  const getHeaderTitle = () => {
    // Not a child or client account
    // eslint-disable-next-line camelcase
    if (accountData.id === user?.account_id) {
      return undefined;
    }
    return accountType === AccountType.AGENCY ? 'Client Companies' : 'Company Locations';
  };

  const [cancelTerminateAccountModalIsOpen, setCancelTerminateAccountModalIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  let terminationRequestDateShort = '---';
  let terminationRequestDateLong = '---';
  let terminationCloseReason = '---';

  if (accountData.closedRequestDate) {
    const dtDateOnly = new Date(accountData.closedRequestDate);
    terminationRequestDateShort = format(dtDateOnly, 'E, MMM d, yyyy');
    terminationRequestDateLong = format(dtDateOnly, 'MMMM d, yyyy');
  }

  let closedDate = 'TBD';
  if (accountData.closedDate) {
    const dtDateOnly = new Date(accountData.closedDate);
    closedDate = format(dtDateOnly, 'E, MMM d, yyyy');
  }

  if (accountData.closedRequestReason) {
    terminationCloseReason = accountData.closedRequestReason ?? '---';
  }

  const summaryTitle = title ?? 'Termination Request Summary';
  const summaryDescription =
    description ??
    HelpTextConstants.TERMINATION_REQUEST_SUMMARY.replace('{{ terminationRequestDate }}', terminationRequestDateLong);

  const printTerminationRequest = (): void => {
    window.print();
  };

  const cancelTermination = () => {
    setCancelTerminateAccountModalIsOpen(true);
  };

  const TerminationSummaryFieldGroups = () => {
    return (
      <FieldGroup className="grid-row">
        <FieldGroup.Field className="tablet:grid-col-6" name="Company ID">
          {accountId}
        </FieldGroup.Field>
        <FieldGroup.Field className="tablet:grid-col-6" name="DUNS Number">
          {accountData?.companyInfo.dunsNumber ?? '---'}
        </FieldGroup.Field>
        <FieldGroup.Field className="tablet:grid-col-6" name="Termination Request Date">
          {terminationRequestDateShort}
        </FieldGroup.Field>
        <FieldGroup.Field className="tablet:grid-col-6" name="Termination Date">
          {closedDate}
        </FieldGroup.Field>
        <FieldGroup.Field className="tablet:grid-col-6" name="Termination Request Reason">
          {terminationCloseReason}
        </FieldGroup.Field>
      </FieldGroup>
    );
  };

  return (
    <FullWidthLayout title={summaryTitle} headerTitle={getHeaderTitle()}>
      <section className="padding-y-4">
        <ErrorBar errorMessage={errorMessage} />
        <h1>{summaryTitle}</h1>
        <p className="margin-top-3 tablet:grid-col-9">{summaryDescription}</p>
        <Alert className="margin-top-3" status="warning">
          <Alert.Heading>You must:</Alert.Heading>
          <div>
            <ol className="usa-list">
              <li>Continue to follow the Memorandum of Understanding</li>
              <li>Resolve all open cases</li>
              <li>Not use E-Verify to create new cases</li>
            </ol>
          </div>
        </Alert>
        <div className="maxw-none">
          <FieldGroup className="grid-row margin-bottom-neg-4">
            <FieldGroup.Field className="tablet:grid-col-6" name="Company Name">
              {accountData?.companyInfo.companyName ?? '---'}
            </FieldGroup.Field>
          </FieldGroup>
          <TerminationSummaryFieldGroups />
          <hr />
          <div className="margin-top-4">
            <Button
              type="button"
              variant="secondary"
              aria-label="Print termination request summary"
              onClick={printTerminationRequest}
            >
              Print Information
            </Button>
            <Button
              type="submit"
              variant="destructive"
              aria-label="Open modal to Cancel Termination Request"
              className="margin-left-1"
              onClick={cancelTermination}
            >
              Cancel Termination Request
            </Button>
          </div>
        </div>
      </section>
      <CancelTerminateAccountModal
        open={cancelTerminateAccountModalIsOpen}
        close={(): void => {
          setCancelTerminateAccountModalIsOpen(false);
        }}
        setErrorMessage={setErrorMessage}
        accountId={accountId}
      />
    </FullWidthLayout>
  );
};

export default TerminationRequestSummary;
